import React, { FC, useEffect, useState } from "react";
import { toast, Icon, Text, List, Checkbox } from "@telia/teddy";
import { ExtraSimType } from "../../../../../models/common";
import { Alert, ModalDialog } from "../../../components";
import { trackWebshopClickEvent } from "../../../google-analytics/googleAnalyticsEventHelper";
import RadioButtonCard from "../../components/molecules/RadioButtonCard/RadioButtonCard";
import "./ExtraSimModal.less";

interface Props {
    extraDataSimPrice?: number;
    extraTvillingSimPrice?: number;
    hasExistingExtraDataSim?: boolean;
    isOnboarding?: boolean;
    onAdd: (simType: ExtraSimType, isEsim: boolean) => void;
    onCancel: () => void;
}

const ExtraSimModal: FC<Props> = ({
    onCancel,
    onAdd,
    extraDataSimPrice,
    extraTvillingSimPrice,
    hasExistingExtraDataSim,
    isOnboarding = false,
}) => {
    const [selectedSimType, setSelectedSimType] = useState<ExtraSimType | undefined>();
    const [esimChecked, setEsimChecked] = useState(false);
    const [showError, setShowError] = useState(false);

    useEffect(() => {
        // Track to GA
        if (hasExistingExtraDataSim) {
            trackWebshopClickEvent({
                action: "click",
                text: "Legg til ekstra SIM",
                type: "button",
                url: "/kasse/medlem",
                context: "extra_sim_unsupported",
            });
        }
    }, []);

    const handleOnSetSelectedSimType = (value: ExtraSimType) => {
        setEsimChecked(false);
        setSelectedSimType(value);
    };

    const handleOnSetEsimChecked = (option: ExtraSimType) => {
        if (selectedSimType !== option) {
            setSelectedSimType(option);
            setEsimChecked(true);
        } else {
            setEsimChecked(!esimChecked);
        }
    };

    const handleOnAddExtraSim = async () => {
        if (!selectedSimType) {
            setShowError(true);
            return;
        }
        const simTypeToDisplay = selectedSimType === "DATASIM" ? "DataSIM" : "TvillingSIM";
        toast.success(`${simTypeToDisplay} lagt til i handlekurv`, {
            position: "top-right",
        });
        try {
            onAdd(selectedSimType, esimChecked);
        } catch {
            // does not need to do anything here => modal will close
        }

        onCancel();
    };

    if (hasExistingExtraDataSim) {
        return (
            <ModalDialog
                name="extra-sim-modal-unsupported"
                closeText="OK"
                heading="Kan ikke legge til ekstra SIM"
                onClose={onCancel}
            >
                <p>
                    Vi kan dessverre ikke å legge til ekstra SIM på denne orderen, men du kan legge det til i ettertid
                    på MinSide.
                </p>
            </ModalDialog>
        );
    }
    return (
        <ModalDialog
            className="extra-sim-modal"
            size="medium"
            name="extra-sim-modal"
            heading="Hva skal du bruke ditt ekstra SIM til?"
            submitText="Legg til i handlekurv"
            submitKind="primary"
            closeText="Avbryt"
            closeKind="secondary"
            onSubmit={handleOnAddExtraSim}
            onClose={onCancel}
        >
            {isOnboarding && <p>Bedriften dekker kostnaden for ekstra SIM.</p>}
            <RadioButtonCard
                selectedValue={selectedSimType}
                setSelectedValue={(value) => handleOnSetSelectedSimType(value as ExtraSimType)}
                options={[
                    {
                        value: "DATASIM",
                        heading: "DataSIM",
                        description: (
                            <>
                                <div className="extra-sim-modal__radio-button-card-description">
                                    <Text mb="100">
                                        Jeg ønsker å bruke{" "}
                                        <span className="extra-sim-modal__radio-button-card-description--bold">
                                            mobildata
                                        </span>{" "}
                                        fra mitt mobilabonnement på flere enheter. Anbefales til:
                                    </Text>
                                    <List layout="vertical" gapSize="small">
                                        <List.Item className="extra-sim-modal__list-item">
                                            <List.Illustration variant="image">
                                                <Icon name="checkmark" aria-hidden="true" />
                                            </List.Illustration>
                                            <List.Content>Nettbrett</List.Content>
                                        </List.Item>
                                        <List.Item className="extra-sim-modal__list-item">
                                            <List.Illustration variant="image">
                                                <Icon name="checkmark" aria-hidden="true" />
                                            </List.Illustration>
                                            <List.Content>PC</List.Content>
                                        </List.Item>
                                    </List>
                                </div>
                                <Checkbox className="extra-sim-modal__checkbox">
                                    <Checkbox.Trigger
                                        checked={esimChecked && selectedSimType === "DATASIM"}
                                        onCheckedChange={() => handleOnSetEsimChecked("DATASIM")}
                                    />
                                    <span>Jeg vil ha eSIM i stedet for fysisk SIM-kort</span>
                                </Checkbox>
                                {extraDataSimPrice !== undefined && (
                                    <div className="extra-sim-modal__price">{extraDataSimPrice},-/md.</div>
                                )}
                            </>
                        ),
                    },
                    {
                        value: "TVILLINGSIM",
                        heading: "TvillingSIM",
                        description: (
                            <div>
                                <div className="extra-sim-modal__radio-button-card-description">
                                    <Text mb="100">
                                        Jeg ønsker å bruke samme mobilnummer (både samtaler, meldinger og data) på{" "}
                                        <span className="extra-sim-modal__radio-button-card-description--bold">
                                            flere enheter
                                        </span>
                                        . Anbefales til:
                                    </Text>
                                    <List layout="vertical" gapSize="small">
                                        <List.Item className="extra-sim-modal__list-item">
                                            <List.Illustration variant="image">
                                                <Icon name="checkmark" aria-hidden="true" />
                                            </List.Illustration>
                                            <List.Content>Ekstra mobiltelefon</List.Content>
                                        </List.Item>
                                        <List.Item className="extra-sim-modal__list-item">
                                            <List.Illustration variant="image">
                                                <Icon name="checkmark" aria-hidden="true" />
                                            </List.Illustration>
                                            <List.Content>Smartklokke</List.Content>
                                        </List.Item>
                                    </List>
                                    <div className="extra-sim-modal__list-item-info">
                                        <Icon name="info" className="extra-sim-modal__list-item-icon" />
                                        <span>
                                            Dersom du trenger SIM til din klokke, anbefaler vi å kjøpe TvillingSIM
                                            gjennom din klokke-app.
                                        </span>
                                    </div>
                                </div>
                                <Checkbox className="extra-sim-modal__checkbox">
                                    <Checkbox.Trigger
                                        checked={esimChecked && selectedSimType === "TVILLINGSIM"}
                                        onCheckedChange={() => handleOnSetEsimChecked("TVILLINGSIM")}
                                    />
                                    <span>Jeg vil ha eSIM i stedet for fysisk SIM-kort</span>
                                </Checkbox>
                                {extraTvillingSimPrice !== undefined && (
                                    <div className="extra-sim-modal__price">{extraTvillingSimPrice},-/md.</div>
                                )}
                            </div>
                        ),
                    },
                ]}
            />
            {showError && <Alert kind="warning">Vennligst velg et alternativ</Alert>}
        </ModalDialog>
    );
};

export default ExtraSimModal;
